import { useState, useEffect } from "react";
import Base from "../../Base/views/Base";
import {
    Box,
    Button,
    Typography,
    TextField,
    CircularProgress,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Snackbar,
    Alert,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import { PDFDownloadLink } from '@react-pdf/renderer';
import BillPDFReport from "./DateRangeReport";
import ExpensePDFReport from "./ExpensePDFReport";
import axiosInstance from "../../../axiosInstance";

const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US') + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US');
};

const ReportLandingPage = () => {
    const userType = localStorage.getItem("billing_app_type");
    const isHotel = userType === "Hotel";
    const isRestaurant = userType === "Restaurant";
    const isHotelAndRestaurant = userType === "Hotel and Restaurant";

    const [tabValue, setTabValue] = useState(0);

    // Bill report state
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [error, setError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [hotelData, setHotelData] = useState(null);

    // Expense report state
    const [expStartDate, setExpStartDate] = useState('');
    const [expEndDate, setExpEndDate] = useState('');
    const [expCategory, setExpCategory] = useState('');
    const [expLoading, setExpLoading] = useState(false);
    const [expReportData, setExpReportData] = useState(null);
    const [expError, setExpError] = useState(null);
    const [expSnackbarOpen, setExpSnackbarOpen] = useState(false);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const hotelID = localStorage.getItem("billing_app_hotel");

    useEffect(() => {
        getHotelData();
    }, []);

    useEffect(() => {
        if (tabValue === 1) {
            axiosInstance(`/api/expense-category/?hotel=${hotelID}`, { method: "GET" })
                .then(res => setExpenseCategories(res.data || []))
                .catch(() => setExpenseCategories([]));
        }
    }, [tabValue, hotelID]);

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const toBase64 = async (url) => {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error converting image to base64:', error);
            return null;
        }
    };

    const getHotelData = async () => {
        try {
            const res = await axiosInstance.get('/api/profile/');
            if (res?.data?.hotel_detail?.logo) {
                const base64Logo = await toBase64(res.data.hotel_detail.logo);
                setHotelData({
                    ...res.data,
                    hotel_detail: {
                        ...res.data.hotel_detail,
                        logoBase64: base64Logo
                    }
                });
            } else {
                setHotelData(res?.data || {});
            }
        } catch (err) {
            console.error('Failed to fetch hotel data:', err);
            setHotelData({});
        }
    };

    const validateDates = () => {
        if (!startDate || !endDate) {
            setError('Please select both start and end dates');
            return false;
        }

        if (new Date(startDate) > new Date(endDate)) {
            setError('End date must be after start date');
            return false;
        }

        const diffTime = Math.abs(new Date(endDate) - new Date(startDate));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 365) {
            setError('Date range cannot exceed 1 year');
            return false;
        }

        return true;
    };

    const generateReport = async () => {
        setError(null);

        if (!validateDates()) return;

        setLoading(true);

        try {
            const response = await axiosInstance.post('/api/bill-report/', {
                start_date: startDate,
                end_date: endDate
                // hotel_id is automatically determined from authenticated user
            });

            if (!response.data || !response.data.bill_details) {
                throw new Error('Invalid report data structure received from server');
            }

            // Don't filter here - let the PDF component handle it
            setReportData(response.data);
            setSnackbarOpen(true);
        } catch (err) {
            setError(err.message || 'Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setStartDate('');
        setEndDate('');
        setReportData(null);
        setError(null);
    };

    const validateExpenseDates = () => {
        if (!expStartDate || !expEndDate) {
            setExpError('Please select both start and end dates');
            return false;
        }
        if (new Date(expStartDate) > new Date(expEndDate)) {
            setExpError('End date must be after start date');
            return false;
        }
        const diffTime = Math.abs(new Date(expEndDate) - new Date(expStartDate));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 365) {
            setExpError('Date range cannot exceed 1 year');
            return false;
        }
        return true;
    };

    const generateExpenseReport = async () => {
        setExpError(null);
        if (!validateExpenseDates()) return;
        setExpLoading(true);
        try {
            const payload = { start_date: expStartDate, end_date: expEndDate };
            if (expCategory) payload.category = parseInt(expCategory, 10);
            const response = await axiosInstance.post('/api/expense-report/', payload);
            setExpReportData(response.data);
            setExpSnackbarOpen(true);
        } catch (err) {
            setExpError(err?.response?.data?.detail || err.message || 'Failed to generate report');
        } finally {
            setExpLoading(false);
        }
    };

    const resetExpenseForm = () => {
        setExpStartDate('');
        setExpEndDate('');
        setExpCategory('');
        setExpReportData(null);
        setExpError(null);
    };

    const handleCloseExpSnackbar = () => setExpSnackbarOpen(false);

    const renderHotelTableHeader = () => (
        <TableRow>
            <TableCell>S.No</TableCell>
            <TableCell>Guest Name</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Check In</TableCell>
            <TableCell>Check Out</TableCell>
            <TableCell>Room</TableCell>
            <TableCell>Billing Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Amount</TableCell>
        </TableRow>
    );

    const renderRestaurantTableHeader = () => (
        <TableRow>
            <TableCell>S.No</TableCell>
            <TableCell>Order ID</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Outlet</TableCell>
            <TableCell>Table</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Amount</TableCell>
        </TableRow>
    );

    const renderHotelTableRow = (bill, i) => (
        <TableRow key={`hotel-${bill.bill_id}-${i}`}>
            <TableCell>{i + 1}</TableCell>
            <TableCell>{bill.reservation?.guests?.name || '-'}</TableCell>
            <TableCell>{bill.reservation?.guests?.phone || '-'}</TableCell>
            <TableCell>{formatShortDate(bill.reservation?.check_in)}</TableCell>
            <TableCell>{formatShortDate(bill.reservation?.check_out)}</TableCell>
            <TableCell>{bill.reservation?.room?.name || '-'}</TableCell>
            <TableCell>{formatDisplayDate(bill.created_at)}</TableCell>
            <TableCell>{bill.status}</TableCell>
            <TableCell align="right">
                Rs.{bill.total_amount?.toFixed(2) || '0.00'}
            </TableCell>
        </TableRow>
    );

    const renderRestaurantTableRow = (bill, i) => (
        <TableRow key={`restaurant-${bill.bill_id}-${i}`}>
            <TableCell>{i + 1}</TableCell>
            <TableCell>{bill.order_id || '-'}</TableCell>
            <TableCell>{bill.customer_name || '-'}</TableCell>
            <TableCell>{bill.customer_email || '-'}</TableCell>
            <TableCell>{bill.customer_phone || '-'}</TableCell>
            <TableCell>{bill.hotel || '-'}</TableCell>
            <TableCell>{bill.table || '-'}</TableCell>
            <TableCell>{formatDisplayDate(bill.created_at)}</TableCell>
            <TableCell>{bill.status}</TableCell>
            <TableCell align="right">
                Rs.{bill.total_amount?.toFixed(2) || '0.00'}
            </TableCell>
        </TableRow>
    );

    // Filter bills for display in the table
    const filteredBills = reportData?.bill_details?.filter(bill => {
        if (isHotel) return bill.reservation !== null;
        if (isRestaurant) return bill.reservation === null;
        return true; // Show all for "Hotel and Restaurant"
    }) || [];

    const hotelBills = reportData?.bill_details?.filter(bill => bill.reservation !== null) || [];
    const restaurantBills = reportData?.bill_details?.filter(bill => bill.reservation === null) || [];

    const totalRevenue = isHotelAndRestaurant ?
        (hotelBills.reduce((sum, bill) => sum + (bill.total_amount || 0), 0) +
            restaurantBills.reduce((sum, bill) => sum + (bill.total_amount || 0), 0)) :
        reportData?.total_revenue || 0;

    const totalBills = isHotelAndRestaurant ?
        hotelBills.length + restaurantBills.length :
        reportData?.total_bills || 0;

    return (
        <Base>
            <Box sx={{ pt: 2, px: 0 }}>
                <Tabs
                    value={tabValue}
                    onChange={(_, v) => setTabValue(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                    <Tab label="Bill Reports" />
                    <Tab label="Expense Reports" />
                </Tabs>

                {tabValue === 0 && (
                <>
                <Box sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        size="small"
                        sx={{ width: 160 }}
                        disabled={loading}
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        size="small"
                        sx={{ width: 160 }}
                        disabled={loading}
                    />
                    <Button
                        variant="contained"
                        onClick={generateReport}
                        disabled={loading}
                        size="small"
                        sx={{ minWidth: 140, height: 36 }}
                    >
                        {loading ? <CircularProgress size={18} /> : 'Generate Report'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={resetForm}
                        disabled={loading}
                        size="small"
                        sx={{ height: 36, minWidth: 80 }}
                    >
                        Reset
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!reportData && !loading && (
                    <Box sx={{
                        mt: 4,
                        textAlign: "center",
                        p: 4,
                        borderRadius: 2,
                        bgcolor: 'background.default'
                    }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Report Generated
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Select date range and click "Generate Report" to view data
                        </Typography>
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {reportData && !loading && (
                    <Paper sx={{ p: 2, mt: 2 }}>
                        {hotelData?.hotel_detail && (
                            <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                                <Typography variant="subtitle1" fontWeight="600" color="primary" gutterBottom>
                                    {hotelData.hotel_detail.name}
                                </Typography>
                                {hotelData.hotel_detail.address && (
                                    <Typography variant="body2" color="text.secondary">{hotelData.hotel_detail.address}</Typography>
                                )}
                                {hotelData.hotel_detail.location && (
                                    <Typography variant="body2" color="text.secondary">{hotelData.hotel_detail.location}</Typography>
                                )}
                                {hotelData.hotel_detail.contact && (
                                    <Typography variant="body2" color="text.secondary">{hotelData.hotel_detail.contact}</Typography>
                                )}
                                <Box component="span" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                                    {hotelData.hotel_detail.gst_number && (
                                        <Typography variant="caption" color="text.secondary">GST: {hotelData.hotel_detail.gst_number}</Typography>
                                    )}
                                    {hotelData.hotel_detail.pan_number && (
                                        <Typography variant="caption" color="text.secondary">PAN: {hotelData.hotel_detail.pan_number}</Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 2,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1.5
                        }}>
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                                    {isHotelAndRestaurant ? "Hotel & Restaurant" : isHotel ? "Hotel" : "Restaurant"}
                                    Bill Report from {formatDisplayDate(startDate)} to {formatDisplayDate(endDate)}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    Total Revenue: Rs.{totalRevenue.toFixed(2)} •
                                    Total Bills: {totalBills}
                                </Typography>
                            </Box>

                            <PDFDownloadLink
                                document={<BillPDFReport
                                    reportData={reportData}
                                    startDate={startDate}
                                    endDate={endDate}
                                    hotelData={hotelData}
                                />}
                                fileName={`${isHotelAndRestaurant ? "hotel_restaurant" : isHotel ? "hotel" : "restaurant"}_bill_report_${startDate}_to_${endDate}.pdf`}
                            >
                                {({ loading: pdfLoading }) => (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        disabled={pdfLoading}
                                        size="small"
                                        sx={{ height: 32 }}
                                    >
                                        {pdfLoading ? 'Preparing PDF...' : 'Download PDF'}
                                    </Button>
                                )}
                            </PDFDownloadLink>
                        </Box>

                        {isHotelAndRestaurant && (
                            <>
                                {hotelBills.length > 0 && (
                                    <>
                                        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontSize: '0.95rem' }}>
                                            Hotel Bills
                                        </Typography>
                                        <TableContainer sx={{ maxHeight: 350, mb: 2 }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    {renderHotelTableHeader()}
                                                </TableHead>
                                                <TableBody>
                                                    {hotelBills.map((bill, i) => renderHotelTableRow(bill, i))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}

                                {restaurantBills.length > 0 && (
                                    <>
                                        <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontSize: '0.95rem' }}>
                                            Restaurant Bills
                                        </Typography>
                                        <TableContainer sx={{ maxHeight: 350 }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    {renderRestaurantTableHeader()}
                                                </TableHead>
                                                <TableBody>
                                                    {restaurantBills.map((bill, i) => renderRestaurantTableRow(bill, i))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}
                            </>
                        )}

                        {!isHotelAndRestaurant && (
                            <TableContainer sx={{ maxHeight: 500 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        {isHotel ? renderHotelTableHeader() : renderRestaurantTableHeader()}
                                    </TableHead>
                                    <TableBody>
                                        {filteredBills.map((bill, i) => (
                                            isHotel ? renderHotelTableRow(bill, i) : renderRestaurantTableRow(bill, i)
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                )}
                </>
                )}

                {tabValue === 1 && (
                <>
                <Box sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={expStartDate}
                        onChange={(e) => setExpStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: 160 }}
                        disabled={expLoading}
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={expEndDate}
                        onChange={(e) => setExpEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: 160 }}
                        disabled={expLoading}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={expCategory}
                            onChange={(e) => setExpCategory(e.target.value)}
                            label="Category"
                            disabled={expLoading}
                        >
                            <MenuItem value="">All</MenuItem>
                            {expenseCategories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={generateExpenseReport}
                        disabled={expLoading}
                        size="small"
                        sx={{ minWidth: 140, height: 36 }}
                    >
                        {expLoading ? <CircularProgress size={18} /> : 'Generate Report'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={resetExpenseForm}
                        disabled={expLoading}
                        size="small"
                        sx={{ height: 36, minWidth: 80 }}
                    >
                        Reset
                    </Button>
                </Box>

                {expError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{expError}</Alert>
                )}

                {!expReportData && !expLoading && (
                    <Box sx={{ mt: 4, textAlign: "center", p: 4, borderRadius: 2, bgcolor: 'background.default' }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Report Generated
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Select date range and click "Generate Report" to view expense data
                        </Typography>
                    </Box>
                )}

                {expLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {expReportData && !expLoading && (
                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                            <Box>
                                <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
                                    Expense Report from {formatShortDate(expStartDate)} to {formatShortDate(expEndDate)}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    Total Expenses: ₹{expReportData.total_amount.toFixed(2)} • Entries: {expReportData.total_count}
                                </Typography>
                            </Box>
                            <PDFDownloadLink
                                document={
                                    <ExpensePDFReport
                                        reportData={expReportData}
                                        startDate={expStartDate}
                                        endDate={expEndDate}
                                    />
                                }
                                fileName={`expense_report_${expStartDate}_to_${expEndDate}.pdf`}
                            >
                                {({ loading: pdfLoading }) => (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        disabled={pdfLoading}
                                        size="small"
                                        sx={{ height: 32 }}
                                    >
                                        {pdfLoading ? 'Preparing PDF...' : 'Download PDF'}
                                    </Button>
                                )}
                            </PDFDownloadLink>
                        </Box>
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>S.No</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Payment</TableCell>
                                        <TableCell>Vendor</TableCell>
                                        <TableCell align="right">Amount (₹)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(expReportData.expense_details || []).map((row, i) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell>{formatShortDate(row.expense_date)}</TableCell>
                                            <TableCell>{row.category_name || '-'}</TableCell>
                                            <TableCell>{row.description || '-'}</TableCell>
                                            <TableCell>{row.payment_mode || '-'}</TableCell>
                                            <TableCell>{row.vendor_name || '-'}</TableCell>
                                            <TableCell align="right">{row.amount?.toFixed(2) || '0.00'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
                </>
                )}

                <Snackbar open={snackbarOpen} autoHideDuration={1000} onClose={handleCloseSnackbar}>
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        Report generated successfully!
                    </Alert>
                </Snackbar>
                <Snackbar open={expSnackbarOpen} autoHideDuration={1000} onClose={handleCloseExpSnackbar}>
                    <Alert onClose={handleCloseExpSnackbar} severity="success" sx={{ width: '100%' }}>
                        Expense report generated successfully!
                    </Alert>
                </Snackbar>
            </Box>
        </Base>
    );
};

export default ReportLandingPage;